import type { ComponentType, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  ChartLineUp,
  ChatCircleText,
  Check,
  Files,
  FileText,
  ListChecks,
  ShieldCheck,
  WarningCircle,
  type IconProps
} from "@phosphor-icons/react";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  type PortalNotification
} from "../data/portalQueries";
import { useAttentionEventTypes } from "../data/overviewQueries";
import { BoxState } from "../ui/BoxState";
import { ClientHead } from "../ui/ClientHead";
import { formatWhen } from "./clientOps/format";
import { notificationDestination } from "./clientOps/notificationDestination";
import "./clientOps/clientOps.css";

/**
 * Notiser, the destination the bell in the shell points at.
 *
 * Everything under this screen already existed: the table, its recipient-only policy, the write
 * gateway, the triggers that insert a row when Petrovic replies or a request moves, and the private
 * realtime topic the shell subscribes to. plan.md section 5.7 makes the in-app channel the permanent
 * base channel, the one that cannot be disabled.
 *
 * Two content rules from `schema.md` section 10 shape the markup. `title` and `body` are fixed
 * sanitized summaries that carry no row data, and `request_number` is a separate column precisely
 * so a reader can see which request moved without the summary ever quoting anything a person wrote.
 * It is rendered as its own element here for the same reason it is on the overview.
 *
 * Drawn after `prototype/kund/pages.js` `notificationsPage` in the second build round of 2026-09-25:
 * one box, one row per notice, and the whole row opens what the notice is about and marks it read.
 * Unread is a bold title, an icon in the info tone and the hidden word "Oläst."; the page carries no
 * unread count, because the bell's dot is the one place that says there is something (design.md
 * section 3, rule 1, and the sketch's decision of 2026-09-24). The introduction went (rule 3).
 */

/**
 * The categories the check constraint allows, in Swedish, each with its icon.
 *
 * A category the database gains later shows no word rather than its raw English key. An unlabelled
 * row still reads correctly, because the title and body carry the meaning; a row labelled
 * `request_update` would put a column value in front of a client.
 */
const CATEGORY: Record<string, { label: string; icon: ComponentType<IconProps> }> = {
  chat_reply: { label: "Chatt", icon: ChatCircleText },
  request_update: { label: "Ärende", icon: ListChecks },
  outage: { label: "Drift", icon: ChartLineUp },
  document: { label: "Dokument", icon: Files },
  report: { label: "Månadsrapport", icon: FileText },
  security: { label: "Säkerhet", icon: ShieldCheck },
  operator_alert: { label: "Driftlarm", icon: WarningCircle }
};

interface RowProps {
  item: PortalNotification;
  destination: string | null;
  onRead: () => void;
}

function NotificationRow({ item, destination, onRead }: RowProps) {
  const unread = item.read_at === null;
  const moment = formatWhen(item.created_at);
  const category = CATEGORY[item.category];
  const Icon = category?.icon ?? Bell;

  const content: ReactNode = (
    <>
      <span className={`type-icon${unread ? " tone-info" : ""}`} aria-hidden="true">
        <Icon size={18} />
      </span>
      <span className="k-row-main">
        {/*
          Three signals for unread, not one. design.md section 5 fixes that colour is never the only
          status signal, so the tinted icon has a heavier title beside it, and the word itself for
          anyone who reaches this by voice.
        */}
        {unread ? <span className="visually-hidden">Oläst.</span> : null}
        {category ? <span className="visually-hidden">{category.label}</span> : null}
        <span className="primary-cell">
          <strong>{item.title}</strong>
          <span>
            {item.request_number === null ? null : (
              <>
                <span data-testid="notification-request-number">REQ-{item.request_number}</span>
                {" · "}
              </>
            )}
            <span>{item.body}</span>
          </span>
        </span>
      </span>
      {moment ? <time dateTime={item.created_at}>{moment}</time> : null}
    </>
  );

  const className = `k-row k-note${unread ? " is-unread" : ""}`;

  return (
    <li data-unread={unread} data-testid="notification-row">
      {destination ? (
        <Link className={className} to={destination} onClick={onRead}>
          {content}
        </Link>
      ) : (
        <div className={className}>{content}</div>
      )}
      {unread ? (
        <button type="button" className="k-icon-button" aria-label="Markera som läst" onClick={onRead}>
          <Check size={18} aria-hidden="true" />
        </button>
      ) : null}
    </li>
  );
}

export function NotificationsRoute() {
  const notifications = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const items = notifications.data ?? [];
  const unreadCount = items.filter((item) => item.read_at === null).length;

  /*
    The security rows' `event_type`, which decides which section of Inställningar a row opens. The
    shared notification read does not select it, so this asks for the one column for those rows
    only, the way Översikt does. Until it answers, or if it fails, a security row opens `/settings`,
    which is where every one of them led before.
  */
  const securityIds = items.filter((item) => item.category === "security").map((item) => item.id);
  const eventTypes = useAttentionEventTypes(securityIds);

  const page = (content: ReactNode) => (
    <div className="k-page">
      <ClientHead
        title="Notiser"
        actions={
          items.length > 0 ? (
            <button
              type="button"
              className="button is-quiet"
              disabled={unreadCount === 0 || markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              <Check size={16} aria-hidden="true" />
              <span>{markAll.isPending ? "Markerar…" : "Markera alla som lästa"}</span>
            </button>
          ) : null
        }
      />
      {content}
    </div>
  );

  const stateBox = (state: ReactNode) => <section className="panel">{state}</section>;

  if (notifications.isLoading) {
    return page(stateBox(<BoxState kind="loading" title="Hämtar notiserna" />));
  }

  if (notifications.isError) {
    return page(
      stateBox(
        <BoxState
          kind="error"
          title="Notiserna gick inte att hämta"
          body="Ingenting har försvunnit. Försök igen, och hör av er i chatten om det upprepas."
          onRetry={() => void notifications.refetch()}
        />
      )
    );
  }

  if (items.length === 0) {
    return page(
      stateBox(
        <BoxState
          kind="empty"
          title="Inget har hänt än"
          body="När vi svarar i chatten, flyttar ett ärende eller lägger upp ett dokument står det här."
        />
      )
    );
  }

  return page(
    <>
      {/*
        A failed mark-all says so and changes nothing else. The rows stay as they are, which is the
        truth: the server refused, so nothing was read. Leaving the button looking finished would be
        the portal claiming a write it did not get.
      */}
      {markAll.isError ? (
        <div className="callout tone-danger" role="alert">
          <WarningCircle size={18} aria-hidden="true" />
          <div>{markAll.error?.message ?? "Notiserna kunde inte markeras. Försök igen."}</div>
        </div>
      ) : null}

      <section className="panel" aria-label="Notiser">
        <ul className="k-rows k-notes">
          {items.map((item) => (
            <NotificationRow
              key={item.id}
              item={item}
              destination={notificationDestination({
                ...item,
                event_type: eventTypes.data?.get(item.id) ?? null
              })}
              onRead={() => {
                // Once only: a second write would move `read_at` to now, and the timestamp would
                // stop meaning when the notice was read and start meaning when it was last opened.
                if (item.read_at === null) {
                  markRead.mutate({ notificationId: item.id });
                }
              }}
            />
          ))}
        </ul>

        {/*
          Said out loud rather than left to be discovered. The query takes the fifty newest, and a
          client who has been with Petrovic for a year will have more than that. An interface that
          quietly stops at a limit teaches a person that older notices were deleted.
        */}
        {items.length >= 50 ? (
          <footer className="panel-foot">
            <span className="quiet k-small">
              De 50 senaste visas här. Äldre notiser finns kvar men syns inte i den här listan.
            </span>
          </footer>
        ) : null}
      </section>
    </>
  );
}
